import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CalendarClock, CheckSquare, Lightbulb, Loader2 } from "lucide-react";

import { cn } from "../../lib/utils";
import { useSaveTakeaway } from "../../hooks/useSaveTakeaway";
import { dueInDays } from "./TodayCard";
import { Button } from "../ui/button";

type CaptureWhen = "today" | "tomorrow" | "next_week" | "next_month";
const WHEN_DAYS: Record<CaptureWhen, number> = {
  today: 0,
  tomorrow: 1,
  next_week: 7,
  next_month: 30,
};

/**
 * PWA share-target landing: anything shared into ClaryNext from the phone's
 * share sheet arrives here, ready to be kept as an insight or dated action.
 * The moment is small on purpose — capture, choose when, get back to life.
 */
const CapturePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const shared = useMemo(() => {
    const parts = [params.get("title"), params.get("text"), params.get("url")]
      .map((s) => s?.trim())
      .filter(Boolean);
    return parts.join("\n");
  }, [params]);

  const [text, setText] = useState(shared);
  const [when, setWhen] = useState<CaptureWhen>("today");
  const [saving, setSaving] = useState(false);
  const saveTakeaway = useSaveTakeaway();

  const save = async (type: "insight" | "action") => {
    const trimmed = text.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    const days = WHEN_DAYS[when];
    await saveTakeaway(type, trimmed, {
      dueDate: type === "action" ? dueInDays(days) : undefined,
      successMessage:
        type === "action" && days > 0 ? t("captured_for_later") : undefined,
      onSuccess: () => navigate("/home"),
      onError: () => setSaving(false),
    });
  };

  return (
    <div className="flex-1 flex items-start justify-center pt-10 px-4">
      <div className="w-full max-w-md rounded-3xl border border-primary/15 bg-card p-6 shadow-sm">
        <h1 className="text-lg font-semibold text-foreground mb-1">{t("quick_capture")}</h1>
        <p className="text-sm text-muted-foreground mb-4">{t("capture_page_subtitle")}</p>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t("whats_on_your_mind")}
          rows={5}
          autoFocus
          className="w-full rounded-xl border border-gray-200 p-3 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
        />
        <div className="flex items-center gap-1.5 mt-3 flex-wrap">
          <CalendarClock className="w-3.5 h-3.5 text-muted-foreground/50" />
          {(Object.keys(WHEN_DAYS) as CaptureWhen[]).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setWhen(key)}
              className={cn(
                "text-[11px] px-2 py-0.5 rounded-full border transition-colors",
                when === key
                  ? "bg-primary/10 border-primary/30 text-primary font-medium"
                  : "bg-transparent border-gray-200 text-muted-foreground/70 hover:border-gray-300"
              )}
            >
              {t(`capture_when_${key}`)}
            </button>
          ))}
        </div>
        <div className="flex flex-col min-[420px]:flex-row items-stretch gap-2 mt-4">
          <Button
            variant="outline"
            disabled={!text.trim() || saving}
            onClick={() => save("insight")}
            className="flex-1 rounded-full border-amber-200 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 me-1.5 animate-spin" />
            ) : (
              <Lightbulb className="w-4 h-4 me-1.5" />
            )}
            {t("save_as_insight")}
          </Button>
          <Button
            disabled={!text.trim() || saving}
            onClick={() => save("action")}
            className="flex-1 rounded-full"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 me-1.5 animate-spin" />
            ) : (
              <CheckSquare className="w-4 h-4 me-1.5" />
            )}
            {t("save_as_action")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CapturePage;
